import { useFetch } from '@brandfolder/react';
import {
  createContext,
  FunctionComponent,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';

import { BrandguideContext } from '@context/brandguide';
import { OAuthContext } from '@context/oauth';
import { PermissionLevels } from '@enums/permission-levels';
import { ApiDatum } from '@typings/api';
import { PermissionLevel } from '@typings/permission-level';
import { poll } from '@utilities/fetch';
import {
  getIsAdmin,
  getIsAnonymous,
  getIsCollaborator,
  getIsGuest
} from '@utilities/permission-level';

import { ConfigContext } from './config';

interface PermissionLevelContextState {
  editable: boolean;
  hasEditPermission: boolean;
  isAdmin: boolean;
  isAnonymous: boolean;
  isCollaborator: boolean;
  isGuest: boolean;
  permissionLevel: PermissionLevels;
  permissionLevelLoading: boolean;
}

const defaultPermissionLevel: PermissionLevelContextState = {
  editable: false,
  isAdmin: false,
  isAnonymous: true,
  isCollaborator: false,
  isGuest: false,
  hasEditPermission: false,
  permissionLevel: PermissionLevels.Empty,
  permissionLevelLoading: false
};

export const PermissionLevelContext =
  createContext<PermissionLevelContextState>(defaultPermissionLevel);

interface PermissionLevelContextProps {
  children: ReactNode;
}

export const PermissionLevelProvider: FunctionComponent<PermissionLevelContextProps> = ({
  children
}) => {
  const { brandguide } = useContext(BrandguideContext);
  const { authed } = useContext(OAuthContext);
  const { publicRuntimeConfig } = useContext(ConfigContext);

  const [isAdmin, setIsAdmin] = useState(defaultPermissionLevel.isAdmin);
  const [isAnonymous, setIsAnonymous] = useState(defaultPermissionLevel.isAnonymous);
  const [isCollaborator, setIsCollaborator] = useState(defaultPermissionLevel.isCollaborator);
  const [isGuest, setIsGuest] = useState(defaultPermissionLevel.isGuest);
  const [hasEditPermission, setHasEditPermission] = useState(
    defaultPermissionLevel.hasEditPermission
  );
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevels>(
    defaultPermissionLevel.permissionLevel
  );
  const [permissionLevelLoading, setPermissionLevelLoading] = useState(
    defaultPermissionLevel.permissionLevelLoading
  );

  const resetPermissionLevel = () => {
    setIsAdmin(getIsAdmin(defaultPermissionLevel.isAdmin));
    setIsAnonymous(getIsAnonymous(defaultPermissionLevel.isAnonymous));
    setIsCollaborator(getIsCollaborator(defaultPermissionLevel.isCollaborator));
    setIsGuest(getIsGuest(defaultPermissionLevel.isGuest));
    setHasEditPermission(defaultPermissionLevel.hasEditPermission);
    setPermissionLevel(defaultPermissionLevel.permissionLevel);
  };

  const { response: token } = useFetch<string>({
    url: '/api/access-token'
  });

  const fetchPermissionLevel = async () => {
    try {
      setPermissionLevelLoading(true);

      const response = await fetch(`/api/v4/brandguides/${brandguide?.key}/permission_level`);
      if (response.ok) {
        const json = (await response.json()) as ApiDatum<PermissionLevel>;
        const level = json.data.id as PermissionLevels;

        setIsAdmin(getIsAdmin(level));
        setIsAnonymous(getIsAnonymous(level));
        setIsCollaborator(getIsCollaborator(level));
        setIsGuest(getIsGuest(level));
        setPermissionLevel(level);
      } else {
        resetPermissionLevel();
      }
    } catch {
      resetPermissionLevel();
    } finally {
      setPermissionLevelLoading(false);
    }
  };

  async function fetchCustomPermissionCheck() {
    try {
      const editPermissionResponse = await fetch(
        `${publicRuntimeConfig.BRANDFOLDER_BOULDER_URL}/api/v4/private/brandguides/${brandguide?.key}/custom_permissions_check/edit_brandguide`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (editPermissionResponse.ok) {
        const editPermissionData = await editPermissionResponse.json();
        setHasEditPermission(editPermissionData.data?.allowed === true);
      }
    } catch {
      setHasEditPermission(false);
    }
  }

  useEffect(() => {
    if (token && isCollaborator && brandguide?.key) {
      fetchCustomPermissionCheck();
    }
  }, [token, isCollaborator, brandguide?.key]);

  useEffect(() => {
    if (authed && brandguide) {
      const fiveMinutes = 5 * 60 * 1000;
      poll(fetchPermissionLevel, fiveMinutes);
    }
  }, [authed, brandguide]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = (isAdmin || (isCollaborator && hasEditPermission)) && authed;

  return (
    <PermissionLevelContext.Provider
      value={{
        editable,
        isAdmin,
        isAnonymous,
        isCollaborator,
        isGuest,
        hasEditPermission,
        permissionLevel,
        permissionLevelLoading
      }}
    >
      {children}
    </PermissionLevelContext.Provider>
  );
};
